import { useSyncExternalStore } from "react";

/**
 * Subscribe to a CSS media query. useSyncExternalStore keeps it correct through
 * concurrent renders and gives the right answer on the very first paint, so the
 * mobile drawer never flashes as a desktop panel.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** Tailwind's `md` breakpoint — the point where the console can float beside the map. */
/**
 * Width alone is not enough: a phone held sideways is 800–915px wide and would
 * qualify, which is both wrong on its face and actively broken. Rotating one
 * used to cross this breakpoint mid-gesture, swapping the mobile sheet for the
 * desktop popup — and since that unmounts the element the browser was showing
 * fullscreen, tapping "fullscreen photo" landed in landscape and then instantly
 * bounced back out.
 *
 * The height floor keeps a landscape phone (~390–430px tall) on the mobile
 * layout while still admitting a portrait tablet at 768×1024.
 */
export const useIsDesktop = (): boolean =>
  useMediaQuery("(min-width: 768px) and (min-height: 500px)");

/**
 * True when the primary input is a finger rather than a cursor. Drives the
 * gesture layer (long-press hints, swipe affordances) — deliberately keyed on
 * pointer type rather than viewport width, since a touchscreen laptop is wide
 * but still wants press-to-reveal.
 */
export const useCoarsePointer = (): boolean => useMediaQuery("(pointer: coarse)");
